"use client"

import {
  Component,
  useCallback,
  useEffect,
  useRef,
  useState,
  type ReactNode,
} from "react"
import dynamic from "next/dynamic"

import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@coval/ui"
import { ChartFigure } from "@coval/ui/chart-figure"
import { AlertCircle, Check, Copy, Maximize2 } from "lucide-react"

import type { ActionExecutionResult } from "../hooks/useActionExecutor"
import { useCoviAnalytics } from "../hooks/useCoviAnalytics"
import type { CovalChartSpecV1 } from "../model/chart/types"
import type {
  CoviActionItem,
  CoviActionItemPatch,
  SofiaProposedAction,
} from "../model/types"
import { DEFAULT_CHART_HEIGHT, parseChartSpec, toCsv } from "../utils/chartSpec"
import { chartSpecSummary, chartSpecTable } from "../utils/chartSpecFigure"
import { titleCase } from "../utils/titleCase"

/**
 * Inline chart card for Sofia's `render_visualization` proposals.
 *
 * Dispatched by the transcript map INSTEAD of ConfirmActionCard — the kind
 * auto-executes (AUTO_EXECUTE_KINDS) but its "execution" is drawing pixels, so
 * there is no confirm UI, no countdown, and no navigate-style receipt line:
 * the chart itself is the receipt. Rendering is STATELESS from the persisted
 * proposal args (never from execution side effects), so hydrated items redraw
 * identically after a reload without re-firing anything. The spec was already
 * fail-closed-validated by parseProposedActions; the re-parse here is a
 * defensive second wall plus how we materialize the typed spec.
 */

/**
 * Names a drawn chart after its title and offers its rows as a table; a stat
 * tile is already text and renders as is.
 */
function VizFigure({
  spec,
  height,
  children,
}: {
  spec: CovalChartSpecV1
  height: number
  children: ReactNode
}) {
  if (spec.chart === "stat") {
    return children
  }
  return (
    <ChartFigure
      label={spec.title}
      summary={chartSpecSummary(spec)}
      table={chartSpecTable(spec)}
      style={{ height }}
    >
      {children}
    </ChartFigure>
  )
}

// Lazy recharts: the chart body only loads when a chart actually arrives.
const CoviVizChartBody = dynamic(
  () => import("./CoviVizChartBody").then((mod) => mod.CoviVizChartBody),
  {
    ssr: false,
    loading: () => (
      <div
        data-testid="covi-viz-loading"
        aria-busy="true"
        className="animate-pulse rounded-md bg-surface-2"
        style={{ height: DEFAULT_CHART_HEIGHT }}
        aria-hidden="true"
      />
    ),
  },
)

/** Chart height inside the expand modal (the card body uses the default). */
const EXPANDED_CHART_HEIGHT = 440

function VizErrorChip() {
  return (
    <div
      data-testid="covi-viz-error-chip"
      className="flex items-center gap-1.5 rounded-md bg-surface-2 px-2.5 py-1.5 text-xs text-coval-text-muted"
    >
      <AlertCircle
        className="h-3.5 w-3.5 shrink-0 text-coval-danger-text"
        aria-hidden="true"
      />
      Couldn&apos;t render this chart
    </div>
  )
}

/**
 * A pathological-but-valid spec must degrade to a compact chip, never crash
 * the panel — recharts throws land here.
 */
class VizErrorBoundary extends Component<
  { children: ReactNode },
  { failed: boolean }
> {
  state = { failed: false }

  static getDerivedStateFromError() {
    return { failed: true }
  }

  render() {
    if (this.state.failed) {
      return <VizErrorChip />
    }
    return this.props.children
  }
}

interface CoviVizCardProps {
  item: CoviActionItem
  execute: (proposal: SofiaProposedAction) => Promise<ActionExecutionResult>
  update: (patch: CoviActionItemPatch) => void
  /** Inline draw height in px; saved notebook surfaces may pass more than the panel default. */
  height?: number
  /**
   * "panel": the chat bubble card. "notebook": borderless, full width of its
   * cell, Title Case heading, no per-point value labels, app palette only.
   */
  variant?: "panel" | "notebook"
}

export function CoviVizCard({
  item,
  execute,
  update,
  height = DEFAULT_CHART_HEIGHT,
  variant = "panel",
}: CoviVizCardProps) {
  const notebook = variant === "notebook"
  const { trackActionConfirmed, trackActionExecuted, trackActionFailed } =
    useCoviAnalytics()
  const [copied, setCopied] = useState(false)
  const [expanded, setExpanded] = useState(false)
  // Re-entry guard: `update` churns identity on every panel render, so the
  // resolve effect below would otherwise re-fire mid-flight.
  const firedRef = useRef(false)

  const { proposal } = item
  const spec = parseChartSpec(proposal.args.spec)

  // Resolve the action funnel on arrival: the no-op executor returns the
  // "Rendered chart: …" receipt detail and the item settles at "done". No
  // freshness gate (unlike navigate) — the execution mutates nothing, and
  // hydrated items normally arrive already resolved.
  const shouldResolve = item.status === "proposed"
  useEffect(() => {
    if (!shouldResolve || firedRef.current) {
      return
    }
    firedRef.current = true
    trackActionConfirmed({
      kind: proposal.kind,
      via: "auto_execute",
      serverExecuted: false,
    })
    void (async () => {
      const result = await execute(proposal)
      if (result.ok) {
        trackActionExecuted({
          kind: proposal.kind,
          via: "auto_execute",
          serverExecuted: false,
        })
        update({ status: "done", detail: result.detail })
      } else {
        trackActionFailed({
          kind: proposal.kind,
          via: "auto_execute",
          serverExecuted: false,
          reason: "execution_error",
          error: result.error,
        })
        update({ status: "error", error: result.error })
      }
    })()
  }, [
    execute,
    proposal,
    shouldResolve,
    trackActionConfirmed,
    trackActionExecuted,
    trackActionFailed,
    update,
  ])

  const handleCopyCsv = useCallback(() => {
    if (!spec || typeof navigator === "undefined" || !navigator.clipboard) {
      return
    }
    void navigator.clipboard
      .writeText(toCsv(spec))
      .then(() => {
        setCopied(true)
        window.setTimeout(() => setCopied(false), 1500)
      })
      .catch(() => {
        // Permission denied / no-op browser — silently skip the feedback tick.
      })
  }, [spec])

  // Upstream validation makes this unreachable in practice (invalid specs are
  // dropped before an item exists) — degrade to the chip, never crash.
  if (!spec) {
    return (
      <div className="ml-9">
        <VizErrorChip />
      </div>
    )
  }

  return (
    <div
      data-testid="covi-viz-card"
      className={
        notebook
          ? "w-full"
          : "ml-9 rounded-lg border border-coval-border bg-surface-1 px-3 py-2.5"
      }
    >
      <div className="text-sm font-medium text-coval-text">
        {notebook ? titleCase(spec.title) : spec.title}
      </div>
      {spec.subtitle ? (
        <div className="mt-0.5 text-xs text-coval-text-muted">
          {spec.subtitle}
        </div>
      ) : null}
      <div className="mt-3">
        <VizErrorBoundary>
          <VizFigure spec={spec} height={height}>
            <CoviVizChartBody
              spec={spec}
              height={height}
              showValueLabels={notebook ? false : undefined}
              paletteOnly={notebook}
            />
          </VizFigure>
        </VizErrorBoundary>
      </div>
      <div className="mt-2 flex items-center gap-3">
        <button
          type="button"
          onClick={handleCopyCsv}
          className="inline-flex items-center gap-1 text-xs text-coval-text-muted transition-colors hover:text-coval-text"
        >
          {copied ? (
            <Check
              className="h-3 w-3 text-coval-success-text"
              aria-hidden="true"
            />
          ) : (
            <Copy className="h-3 w-3" aria-hidden="true" />
          )}
          {copied ? "Copied" : "Copy CSV"}
        </button>
        {!notebook ? (
          <button
            type="button"
            onClick={() => setExpanded(true)}
            className="inline-flex items-center gap-1 text-xs text-coval-text-muted transition-colors hover:text-coval-text"
          >
            <Maximize2 className="h-3 w-3" aria-hidden="true" />
            Expand
          </button>
        ) : null}
      </div>
      <Dialog open={expanded} onOpenChange={setExpanded}>
        <DialogContent size="lg" className="max-w-4xl">
          <DialogHeader>
            <DialogTitle>{spec.title}</DialogTitle>
            {spec.subtitle ? (
              <DialogDescription>{spec.subtitle}</DialogDescription>
            ) : null}
          </DialogHeader>
          <DialogBody>
            <VizErrorBoundary>
              <VizFigure spec={spec} height={EXPANDED_CHART_HEIGHT}>
                <CoviVizChartBody spec={spec} height={EXPANDED_CHART_HEIGHT} />
              </VizFigure>
            </VizErrorBoundary>
          </DialogBody>
        </DialogContent>
      </Dialog>
    </div>
  )
}
